import React from 'react';
import { ChartConfig } from '../../types/AiSql';

interface AiSqlChartProps {
  chart?: ChartConfig;
  rows: Record<string, any>[];
}

const numberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});

export const AiSqlChart: React.FC<AiSqlChartProps> = ({ chart, rows }) => {
  if (!chart || chart.type === 'table_only') {
    return null;
  }

  const xKey = chart.x;
  const yKey = chart.y;
  if (!yKey) {
    return null;
  }

  type DataPoint = { x: any; y: number; series?: any };

  const dataset: DataPoint[] = rows
    .map((row, index): DataPoint | null => {
      const yValue = Number(row[yKey]);
      if (Number.isNaN(yValue)) {
        return null;
      }
      return {
        x: xKey ? row[xKey] : index + 1,
        y: yValue,
        series: chart.series ? row[chart.series] : undefined,
      };
    })
    .filter((item): item is DataPoint => item !== null)
    .slice(0, 50);

  if (dataset.length === 0) {
    return null;
  }

  switch (chart.type) {
    case 'bar':
      return renderBarChart(dataset, chart);
    case 'line':
      return renderLineChart(dataset, chart);
    case 'pie':
      return renderPieChart(dataset, chart);
    default:
      return null;
  }
};

function renderBarChart(
  data: { x: any; y: number; series?: any }[],
  chart: ChartConfig
): React.ReactElement {
  const maxValue = Math.max(...data.map((d) => d.y));
  return (
    <div className="ai-chart ai-chart--bar">
      {chart.title && <h3>{chart.title}</h3>}
      <div className="ai-chart__bars">
        {data.map((d, index) => (
          <div className="ai-chart__bar-row" key={`${d.x}-${index}`}>
            <span className="ai-chart__bar-label">
              {chart.series && d.series ? `${d.series} – ` : ''}
              {String(d.x)}
            </span>
            <div className="ai-chart__bar-track">
              <div className="ai-chart__bar-fill" style={{ width: `${(d.y / maxValue) * 100}%` }} />
            </div>
            <span className="ai-chart__bar-value">{numberFormatter.format(d.y)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderLineChart(
  data: { x: any; y: number; series?: any }[],
  chart: ChartConfig
): React.ReactElement {
  const width = 600;
  const height = 240;
  const maxY = Math.max(...data.map((d) => d.y));
  const minY = Math.min(...data.map((d) => d.y));
  const points = data.map((d, index) => {
    const xPos = (index / (data.length - 1 || 1)) * (width - 40) + 20;
    const yPos = height - ((d.y - minY) / (maxY - minY || 1)) * (height - 40) - 20;
    return `${xPos},${yPos}`;
  });

  return (
    <div className="ai-chart ai-chart--line">
      {chart.title && <h3>{chart.title}</h3>}
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Line chart">
        <polyline fill="none" stroke="#3b82f6" strokeWidth="3" points={points.join(' ')} />
        {data.map((d, index) => {
          const [xPos, yPos] = points[index].split(',').map(Number);
          return (
            <g key={`${d.x}-${index}`}>
              <circle cx={xPos} cy={yPos} r={4} fill="#1d4ed8" />
            </g>
          );
        })}
      </svg>
      <div className="ai-chart__line-labels">
        {data.map((d, index) => (
          <span key={`${d.x}-${index}`}>{String(d.x)}</span>
        ))}
      </div>
    </div>
  );
}

function renderPieChart(
  data: { x: any; y: number; series?: any }[],
  chart: ChartConfig
): React.ReactElement {
  const total = data.reduce((sum, item) => sum + item.y, 0);
  if (total <= 0) {
    return <div className="ai-chart ai-chart--pie">No positive values to chart.</div>;
  }
  let cumulative = 0;
  const segments = data.map((item, index) => {
    const start = (cumulative / total) * 360;
    cumulative += item.y;
    const end = (cumulative / total) * 360;
    const color = PIE_COLORS[index % PIE_COLORS.length];
    return `${color} ${start}deg ${end}deg`;
  });

  return (
    <div className="ai-chart ai-chart--pie">
      {chart.title && <h3>{chart.title}</h3>}
      <div
        className="ai-chart__pie"
        style={{ background: `conic-gradient(${segments.join(',')})` }}
      />
      <ul className="ai-chart__legend">
        {data.map((item, index) => (
          <li key={`${item.x}-${index}`}>
            <span
              className="ai-chart__legend-swatch"
              style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
            />
            <span className="ai-chart__legend-label">{String(item.x)}</span>
            <span className="ai-chart__legend-value">{((item.y / total) * 100).toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const PIE_COLORS = ['#2563eb', '#7c3aed', '#059669', '#f97316', '#dc2626', '#0ea5e9'];

export default AiSqlChart;
